"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, Layers, Clock, FileCheck2, ArrowRight, ShieldAlert, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface RiskItem {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  impact: string;
}
const risksData: RiskItem[] = [{
  id: 1,
  title: "Thief Sampling Bias",
  subtitle: "Distortion of Blend Integrity During Extraction",
  description: "Physical sample thieves disturb static powder beds, preferentially drawing fine particles and falsifying true blend homogeneity profiles.",
  impact: "High risk of false-positive or false-negative uniformity results before compression."
}, {
  id: 2,
  title: "Blend Segregation Post-Mix",
  subtitle: "Particle De-mixing During Discharge & Transfer",
  description: "Gravitational drop and vibration during intermediate bulk container (IBC) discharge create localized potency variations across batches.",
  impact: "Uncontrolled batch variability during tableting or encapsulation operations."
}, {
  id: 3,
  title: "Off-Line Analytical Lag",
  subtitle: "Extended Hold Times and Quarantine Bottlenecks",
  description: "Manual laboratory HPLC testing creates multi-day testing queues, holding equipment idle while batches wait for release clearance.",
  impact: "Constrained production throughput and high operating inventory costs."
}, {
  id: 4,
  title: "Regulatory Audit Scrutiny",
  subtitle: "Stringent FDA & EMA Expectations for Real-Time Uniformity",
  description: "Regulatory inspectors increasingly challenge traditional stratified sampling plans that lack continuous, representative process analytical data.",
  impact: "Potential warning letters, batch rejections, and extensive remediation delays."
}];
export default function BlendRisks() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="blendsamplingrisks" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column (8 cols): Title & 4 Stacked Risk Cards */}
          <div className="lg:col-span-8 space-y-8">
            <div className="space-y-4">
              <Badge variant="outline" className="border-primary/40 text-foreground bg-primary/5">
                Process Risk Assessment
              </Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
                The Risks of Conventional Blend Sampling and Content Non-Uniformity
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground font-sans max-w-3xl leading-relaxed">
                Traditional manual blend sampling methods present systematic vulnerabilities in solid dose manufacturing. Invasive techniques compromise sample integrity and fail to provide continuous process verification.
              </p>
            </div>

            {/* Stacked Risk Cards */}
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-60px"
          }} className="space-y-4">
              {risksData.map((risk, index) => <motion.div key={risk.id} data-index={index} variants={itemVariants}>
                  <Card className="border border-border border-l-4 border-l-primary bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2.5">
                            <span className="flex items-center justify-center size-6 rounded-md bg-secondary text-secondary-foreground text-xs font-semibold">
                              0{risk.id}
                            </span>
                            <CardTitle className="text-xl sm:text-2xl font-default text-foreground">
                              {risk.title}
                            </CardTitle>
                          </div>
                          <CardDescription className="text-xs sm:text-sm font-medium text-muted-foreground">
                            {risk.subtitle}
                          </CardDescription>
                        </div>
                        <div className="text-primary mt-1 shrink-0">
                          {risk.id === 1 && <Layers className="size-5" />}
                          {risk.id === 2 && <AlertTriangle className="size-5" />}
                          {risk.id === 3 && <Clock className="size-5" />}
                          {risk.id === 4 && <ShieldAlert className="size-5" />}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3 pt-0">
                      <p className="text-sm sm:text-base text-foreground font-sans leading-relaxed">
                        {risk.description}
                      </p>
                      <div className="bg-secondary/60 rounded-md p-3 text-xs sm:text-sm border border-border/70 flex items-start gap-2 text-secondary-foreground">
                        <span className="font-semibold shrink-0 text-primary">Critical Impact:</span>
                        <span>{risk.impact}</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </motion.div>
          </div>

          {/* Right Column (4 cols): Summary, Regulatory Alert & Action Card */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
            <Card className="border border-border bg-card shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary mb-1">
                  <ShieldAlert className="size-5" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Regulatory Mandate
                  </span>
                </div>
                <CardTitle className="text-xl font-default text-foreground">
                  Compliance and Release Impact
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground font-sans">
                  Current GMP and ICH Q8 guidelines emphasize direct, non-destructive blend uniformity verification.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3 text-sm font-sans">
                  <div className="flex items-start gap-2.5">
                    <FileCheck2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-muted-foreground text-xs sm:text-sm">
                      <strong className="text-foreground font-medium">ICH Q8 & Q13:</strong> Continuous manufacturing standards require robust in-line analytical verification.
                    </p>
                  </div>
                  <Separator />
                  <div className="flex items-start gap-2.5">
                    <HelpCircle className="size-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-muted-foreground text-xs sm:text-sm">
                      <strong className="text-foreground font-medium">PAT Real-Time Testing:</strong> Replaces destructive sampling with continuous optical monitoring.
                    </p>
                  </div>
                </div>

                <div className="pt-3 border-t border-border space-y-3">
                  <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/SolutionsPage">
                      <span>Explore PAT Solutions</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full border-border text-foreground hover:bg-secondary">
                    <Link to="/ContactPage">
                      <span>Request Technical Consultation</span>
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick summary statistic card */}
            <div className="rounded-lg bg-secondary p-5 border border-border text-secondary-foreground space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Analytical Efficiency
              </p>
              <p className="text-2xl font-bold font-default text-foreground">
                {"Up to 90% Faster"}
              </p>
              <p className="text-xs text-muted-foreground leading-normal font-sans">
                Real-time spectroscopy eliminates sampling thief disruption and multi-day laboratory release backlogs.
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>;
}